/**
 * =============================================================================
 * Componente: AffiliateProgressStep
 *
 * Step de acompanhamento de progresso da importação de afiliados.
 * Espelha visual e funcionalmente o Step6Progress do CRM ("Negócios"),
 * adaptado aos campos do enum `affiliate_import_status` e às colunas
 * agregadas em `affiliate_import_jobs`.
 *
 * Renderiza:
 *   - Barra de progresso ao vivo (succeeded + failed) / total_rows
 *   - Contadores de sucesso/inválidos/falhas/existentes + em fila/processando/atrasados
 *   - Banner amber quando há lote atrasado (heurística de stalled)
 *   - Banner muted quando não há evolução há > 30s
 *   - Banner destructive com o último erro de lote
 *   - Aba "Erros" em modo leitura (top 100 itens problemáticos)
 *   - Botão "Cancelar importação" no header (status cancelável) — delegado
 *     ao componente pai via onCancel (que abre o AlertDialog de confirmação)
 *
 * CHANGELOG:
 *   RELEASE: 2026-05-17 v1.0.0 - Versão inicial. Reusa exatamente a UX do
 *            Step6Progress.tsx do CRM para garantir paridade visual entre
 *            os dois fluxos de importação.
 * =============================================================================
 */

import { Loader2, CheckCircle2, AlertTriangle, Clock, XCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Stat } from '@/components/crm/import/steps/Stat';

// Status em que o usuário ainda pode cancelar (alinhado com a RPC
// cancel_affiliate_import_job, que aceita estes mesmos estados).
const CANCELLABLE_STATUSES = new Set([
  'validating',
  'validated',
  'awaiting_approval',
  'enqueued',
  'processing',
]);

// Tradução dos status do enum `affiliate_import_status` para o cabeçalho.
const STATUS_LABELS_PT: Record<string, string> = {
  draft: 'Rascunho',
  validating: 'Validando',
  validated: 'Validado',
  awaiting_approval: 'Aguardando aprovação',
  enqueued: 'Na fila',
  processing: 'Processando',
  completed: 'Concluído',
  completed_with_errors: 'Concluído com erros',
  failed: 'Falhou',
  cancelled: 'Cancelado',
};

interface AffiliateProgressStepProps {
  /** True enquanto o primeiro fetch da query de detalhes está em curso. */
  isLoading: boolean;
  /**
   * Payload normalizado por useAffiliateImport().useJobDetails():
   * { job, batches, items, batches_summary, last_batch_update, last_batch_error }
   */
  details: any | null | undefined;
  /** ID do job em acompanhamento — usado para chaves de cache. */
  jobId: string | null;
  /** Erro do polling (caso a RPC falhe). */
  error?: unknown;
  /**
   * Abre o AlertDialog de confirmação de cancelamento. Quando omitido, o
   * botão "Cancelar importação" não é renderizado (modo somente leitura).
   */
  onCancel?: () => void;
  /** Spinner no botão de cancelar enquanto a mutation está em curso. */
  cancelPending?: boolean;
}

export default function AffiliateProgressStep({
  isLoading,
  details,
  jobId,
  error,
  onCancel,
  cancelPending,
}: AffiliateProgressStepProps) {
  // -------- Estados de loading / erro / vazio --------
  if (isLoading && !details) {
    return (
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
        <p className="text-sm">Carregando detalhes do job…</p>
      </div>
    );
  }

  if (error) {
    const msg = (error as any)?.message ?? String(error);
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 flex gap-3">
        <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0" />
        <div className="text-sm min-w-0">
          <p className="font-medium text-destructive">Falha ao carregar progresso</p>
          <p className="text-muted-foreground mt-1 break-all">{msg}</p>
        </div>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
        Aguardando dados do job…
      </div>
    );
  }

  // -------- Derivações comuns --------
  const job = details?.job ?? {};
  const summary = details?.batches_summary ?? {};
  const items = (details?.items ?? []) as Array<{
    row_index: number;
    status: string;
    email: string | null;
    errors: any;
    last_error: string | null;
  }>;

  // Progresso por LINHAS processadas (succeeded + failed). Espelha exatamente
  // a fórmula do Step6Progress de Negócios, mas usando os contadores próprios
  // de affiliate_import_jobs.
  const totalRows = Number(job.total_rows ?? 0);
  const succeeded = Number(job.succeeded_count ?? 0);
  const failed = Number(job.failed_count ?? 0);
  const existing = Number(job.existing_count ?? 0);
  const invalid = Number(job.invalid_count ?? 0);
  const duplicate = Number(job.duplicate_count ?? 0);
  const validCount = Number(job.valid_count ?? totalRows);

  const processed = succeeded + failed;
  const pct = totalRows > 0 ? Math.round((processed / totalRows) * 100) : 0;
  const isComplete =
    job.status === 'completed' ||
    job.status === 'completed_with_errors' ||
    job.status === 'failed' ||
    job.status === 'cancelled';

  // Sinais de saúde do processamento (mesma lógica do Step6Progress)
  const stalled = Number(summary.stalled ?? 0);
  const enqueued = Number(summary.enqueued ?? 0);
  const processing = Number(summary.processing ?? 0);
  const lastBatchUpdate = details?.last_batch_update ?? null;
  const lastBatchError = details?.last_batch_error ?? null;

  const lastUpdateAgeSec = lastBatchUpdate
    ? Math.max(0, Math.floor((Date.now() - new Date(lastBatchUpdate).getTime()) / 1000))
    : null;
  const noProgressForAWhile =
    !isComplete &&
    (enqueued > 0 || processing > 0) &&
    lastUpdateAgeSec !== null &&
    lastUpdateAgeSec > 30;

  // Itens na fila = total válido - já processado (limite mínimo zero).
  const itemsInQueue = Math.max(0, validCount - succeeded - failed);

  return (
    <div className="flex flex-col h-full min-h-0 min-w-0 gap-4">
      {/* Cabeçalho fixo: status + botão cancelar */}
      <div className="shrink-0 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {isComplete ? (
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
            ) : (
              <Loader2 className="h-5 w-5 animate-spin flex-shrink-0" />
            )}
            <span className="font-medium truncate">
              {STATUS_LABELS_PT[job.status] || job.status || '—'}
            </span>
          </div>

          {/* Botão de cancelar (delegado ao pai via onCancel) */}
          {onCancel && CANCELLABLE_STATUSES.has(job.status) && (
            <Button
              size="sm"
              variant="outline"
              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive flex-shrink-0"
              onClick={onCancel}
              disabled={cancelPending}
              title="Cancelar esta importação"
            >
              {cancelPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4 mr-1.5" />
              )}
              Cancelar importação
            </Button>
          )}
        </div>

        <Progress value={pct} />
        <p className="text-sm text-muted-foreground">
          {processed.toLocaleString('pt-BR')} de {totalRows.toLocaleString('pt-BR')} afiliados
          processados ({pct}%)
        </p>

        {/* Banner de recuperação automática */}
        {stalled > 0 && (
          <div className="rounded-lg border border-amber-300/40 bg-amber-50 dark:bg-amber-950/20 p-3 flex gap-3">
            <Clock className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs">
              <p className="font-medium text-amber-900 dark:text-amber-200">
                Recuperando {stalled} lote{stalled > 1 ? 's' : ''} atrasado
                {stalled > 1 ? 's' : ''}
              </p>
              <p className="text-amber-800/80 dark:text-amber-200/70 mt-0.5">
                O sistema detectou lentidão e está reagendando automaticamente.
                Nenhuma ação é necessária.
              </p>
            </div>
          </div>
        )}

        {/* Banner de "sem evolução recente" */}
        {stalled === 0 && noProgressForAWhile && (
          <div className="rounded-lg border border-muted bg-muted/30 p-3 flex gap-3">
            <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="text-xs text-muted-foreground">
              Aguardando próximo ciclo do worker (~5s). Sem evolução há {lastUpdateAgeSec}s.
            </div>
          </div>
        )}

        {/* Banner de último erro de lote */}
        {lastBatchError && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 flex gap-3">
            <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
            <div className="text-xs min-w-0">
              <p className="font-medium text-destructive">Último erro de lote</p>
              <p className="text-muted-foreground mt-0.5 break-all">{lastBatchError}</p>
            </div>
          </div>
        )}
      </div>

      {/* Tabs: Resumo + Erros */}
      <Tabs defaultValue="summary" className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden">
        <TabsList className="shrink-0 self-start">
          <TabsTrigger value="summary">Resumo</TabsTrigger>
          <TabsTrigger value="errors">Erros ({items.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="flex-1 min-h-0 min-w-0 overflow-y-auto mt-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm rounded-lg border p-4">
            <Stat label="Sucessos" value={succeeded} variant="success" />
            <Stat label="Inválidos" value={invalid + duplicate} variant="warning" />
            <Stat label="Falhas" value={failed} variant="destructive" />
            <Stat label="Existentes" value={existing} />
            <Stat label="Itens na fila" value={itemsInQueue} />
            <Stat label="Lotes em fila" value={enqueued} />
            <Stat label="Lotes processando" value={processing} />
            <Stat
              label="Lotes atrasados"
              value={stalled}
              variant={stalled > 0 ? 'warning' : undefined}
            />
          </div>
        </TabsContent>

        {/* Aba de erros — somente leitura. Reprocesso individual ficará em
            entrega futura (depende de RPC requeue_affiliate_import_item). */}
        <TabsContent
          value="errors"
          className="flex-1 min-h-0 min-w-0 mt-3 flex flex-col overflow-hidden"
        >
          <div className="flex-1 min-h-0 min-w-0 rounded-lg border overflow-auto">
            <Table className="min-w-[640px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Linha</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead className="min-w-[200px]">E-mail</TableHead>
                  <TableHead className="min-w-[280px]">Motivo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                      Sem erros para exibir.
                    </TableCell>
                  </TableRow>
                )}
                {items.map((it) => {
                  // Mensagem amigável: prioriza validation_errors (estruturado),
                  // depois last_error textual.
                  const reason = it.errors
                    ? typeof it.errors === 'string'
                      ? it.errors
                      : Array.isArray(it.errors)
                        ? it.errors.join(', ')
                        : JSON.stringify(it.errors)
                    : it.last_error ?? '—';
                  return (
                    <TableRow key={`${it.row_index}-${it.status}`}>
                      <TableCell className="font-mono text-xs">{it.row_index + 1}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {STATUS_LABELS_PT[it.status] || it.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="max-w-[240px] truncate" title={it.email ?? ''}>
                          {it.email || '—'}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="max-w-[420px] truncate" title={reason}>
                          {reason}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
